import type { DateIso } from '../../../types/domain';
import { addDays, dateFromIso } from '../base';

// Durée par défaut d'un contrat récurrent sans date de fin : un an à partir de son début
// (règle métier du 2026-09-28 : le total et la somme des années doivent coïncider).
export const OPEN_CONTRACT_MONTHS = 12;

// Date de début du k-ième mois d'un contrat (mois anniversaires : 15/10, 15/11…), le jour étant
// ramené au dernier jour du mois quand il n'existe pas (31/01 → 28/02).
function monthStart(start: Date, k: number): Date {
  const y = start.getFullYear(),
    m = start.getMonth() + k;
  const lastDay = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(start.getDate(), lastDay));
}

// Contrat (CRM et phases) : un début au dernier jour de son mois a ses échéances au dernier jour de chaque
// mois (31/01 → 28/02 = 1 mois, 31/01 → 31/03 = 2 mois, 30/04 → 31/05 = 1 mois) ; sinon la k-ième
// échéance est la veille du k-ième mois anniversaire (15/10 → 14/11).
const isLastDayOfMonth = (d: Date) => addDays(d, 1).getDate() === 1;

export function contractPeriodEnd(start: Date, k: number): Date {
  if (isLastDayOfMonth(start)) return new Date(start.getFullYear(), start.getMonth() + k + 1, 0);
  return addDays(monthStart(start, k), -1);
}

// Début du k-ième mois du contrat (k = 0 pour le premier) : lendemain de l'échéance précédente.
export function contractMonthStart(start: Date, k: number): Date {
  return k === 0 ? start : addDays(contractPeriodEnd(start, k), 1);
}

// Nombre de mois d'un contrat [s, e] bornes incluses : un mois commencé compte pour un mois.
function contractSpan(s: Date, e: Date): number {
  let n = Math.max(1, (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) - 1);
  while (contractPeriodEnd(s, n) < e) n++;
  while (n > 1 && contractPeriodEnd(s, n - 1) >= e) n--;
  return n;
}

// Nombre de mois du contrat [debut,fin] qui tombent dans l'année `year` : chaque mois anniversaire
// est rattaché à l'année où il commence, pour que la somme des années égale la durée totale.
// Fin absente → openMonths mois à partir du début (engagement saisi, sinon OPEN_CONTRACT_MONTHS).
export function contractMonthsInYear(
  startIso: DateIso | null | undefined,
  endIso: DateIso | null | undefined,
  year: number | string,
  openMonths: number = OPEN_CONTRACT_MONTHS,
): number {
  if (!startIso) return 0;
  const start = dateFromIso(startIso);
  if (!start) return 0;
  const total = endIso ? contractTotalMonths(startIso, endIso) : openMonths;
  const y = Number(year);
  let n = 0;
  for (let k = 0; k < total; k++) if (contractMonthStart(start, k).getFullYear() === y) n++;
  return n;
}

// Nombre total de mois du contrat (bornes incluses). Fin absente → 0 (durée inconnue).
export function contractTotalMonths(
  startIso: DateIso | null | undefined,
  endIso: DateIso | null | undefined,
): number {
  if (!startIso || !endIso) return 0;
  const s = dateFromIso(startIso),
    e = dateFromIso(endIso);
  if (!s || !e || e < s) return 0;
  return contractSpan(s, e);
}
